import { useRef } from "react";

import { Button } from "src/components/Button";
import { Dialog } from "src/components/Dialog";

export interface BlockNavigationAlertProps {
  /** Whether the alert dialog is open. */
  isOpen?: boolean;
  /** Called when the dialog should close. */
  onClose?: () => void;
  /** Called when the user chooses to stay. */
  onSubmit?: () => void;
  /** Called when the user chooses to discard and leave. */
  onDiscardChanges?: () => void;
  /** Custom title for the alert. */
  title?: string;
  /** Custom message for the alert body. */
  message?: string;
  /** Custom label for the discard/cancel button. */
  cancelButtonLabel?: string;
  /** Custom label for the stay/submit button. */
  submitButtonLabel?: string;
}

const BlockNavigationAlert = ({
  isOpen = false,
  onClose,
  onSubmit,
  onDiscardChanges,
  title = "You have unsaved changes",
  message = "Are you sure you want to leave? All unsaved changes will be lost.",
  cancelButtonLabel = "Discard and leave",
  submitButtonLabel = "Stay on this page",
}: BlockNavigationAlertProps) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose ?? (() => {})}
      closeButton
      closeOnEsc
      closeOnOutsideClick
      initialFocusRef={submitButtonRef}
      size="medium"
      data-testid="alert-box"
    >
      <Dialog.Header>
        <Dialog.Title data-testid="alert-title">{title}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Body>
        <p
          data-testid="alert-message"
          className="text-sm text-muted-foreground"
        >
          {message}
        </p>
      </Dialog.Body>
      <Dialog.Footer className="flex items-center justify-end gap-2">
        <Button
          data-testid="alert-cancel-button"
          label={cancelButtonLabel}
          variant="destructive"
          onClick={onDiscardChanges}
        />
        <Button
          data-testid="alert-submit-button"
          label={submitButtonLabel}
          ref={submitButtonRef}
          onClick={onSubmit}
        />
      </Dialog.Footer>
    </Dialog>
  );
};

BlockNavigationAlert.displayName = "BlockNavigationAlert";

export { BlockNavigationAlert };
